// Цифровые UGC-артикулы в консоли (ТЗ 31.10.2026, раздел 4.7): реестр всех
// артикулов площадки, карточка, платформенные коды под акции OSMIX, сводка и
// отметка о выплате комиссии. Схемы - из @osmix/contracts (ugc-articles.ts),
// здесь только пространство `api.ugc.*` и его http-реализация; мок живёт в
// ./mock/ugc.ts.
//
// Импорт из корневого barrel @osmix/contracts, а не из subpath
// @osmix/contracts/ugc-articles: dev-бандлер консоли резолвит subpath-exports
// по снимку из lockfile, а barrel-реэкспорт - по относительному пути.
import {
	UgcAnalyticsSchema,
	UgcArticleDetailSchema,
	UgcArticleSchema,
	UgcCommissionSchema,
	type UgcAnalytics,
	type UgcArticle,
	type UgcArticleDetail,
	type UgcArticleInput,
	type UgcArticlePatch,
	type UgcArticleStatus,
	type UgcCommission,
	type UgcOwnerKind,
} from "@osmix/contracts";

export {
	UGC_ARTICLE_LENGTH,
	UGC_ATTRIBUTION_DAYS,
	UGC_RETURN_WINDOW_DAYS,
	UGC_TEXTS,
	UgcArticleCodeSchema,
} from "@osmix/contracts";
export type {
	UgcAnalytics,
	UgcArticle,
	UgcArticleInput,
	UgcArticlePatch,
	UgcArticleStatus,
	UgcBalance,
	UgcChannel,
	UgcCommission,
	UgcCommissionState,
	UgcMetrics,
	UgcOwnerKind,
	UgcPartnerRole,
} from "@osmix/contracts";

/** Карточка артикула: у заказа есть commissionId для ручки «Выплачено». */
export const UgcAdminArticleDetailSchema = UgcArticleDetailSchema;
export type UgcAdminArticleOrder = UgcArticleDetail["orders"][number];
export type UgcAdminArticleDetail = UgcArticleDetail;
export type UgcArticleEvent = UgcArticleDetail["events"][number];

export type UgcAdminListQuery = {
	/** Номер артикула, продавец, партнёр, товар, название. */
	q?: string;
	shopId?: string;
	partnerId?: string;
	productId?: string;
	status?: UgcArticleStatus;
	ownerKind?: UgcOwnerKind;
	page: number;
	pageSize: number;
};

export type UgcArticlePage = { items: UgcArticle[]; total: number };

/** Пустые from/to - всё время. */
export type UgcPeriod = { from?: string; to?: string };

export type UgcAdminApi = {
	list(query: UgcAdminListQuery): Promise<UgcArticlePage>;
	get(id: string): Promise<UgcAdminArticleDetail>;
	/** Платформенный артикул под акцию OSMIX (ownerKind platform). */
	create(input: UgcArticleInput): Promise<UgcArticle>;
	/** Правка и принудительная смена статуса; для blocked причина обязательна. */
	update(id: string, patch: UgcArticlePatch): Promise<UgcArticle>;
	summary(period: UgcPeriod): Promise<UgcAnalytics>;
	/** Отметка «Выплачено» по комиссии в состоянии available. */
	markPaid(commissionId: string): Promise<UgcCommission>;
};

type Requester = <T>(
	path: string,
	opts?: { method?: string; query?: Record<string, string | number | undefined>; body?: unknown },
) => Promise<T>;

const enc = encodeURIComponent;

const parsePage = (raw: unknown): UgcArticlePage => {
	const body = (raw ?? {}) as { items?: unknown; total?: unknown };
	const items = UgcArticleSchema.array().parse(body.items ?? []);
	const total = typeof body.total === "number" ? body.total : items.length;
	return { items, total };
};

export const createUgcHttp = (request: Requester): UgcAdminApi => ({
	list: async (query) =>
		parsePage(
			await request<unknown>("/admin/ugc/articles", {
				query: {
					q: query.q || undefined,
					shopId: query.shopId || undefined,
					partnerId: query.partnerId || undefined,
					productId: query.productId || undefined,
					status: query.status || undefined,
					ownerKind: query.ownerKind || undefined,
					page: query.page,
					pageSize: query.pageSize,
				},
			}),
		),
	get: async (id) => UgcAdminArticleDetailSchema.parse(await request<unknown>(`/admin/ugc/articles/${enc(id)}`)),
	create: async (input) =>
		UgcArticleSchema.parse(await request<unknown>("/admin/ugc/articles", { method: "POST", body: input })),
	update: async (id, patch) =>
		UgcArticleSchema.parse(await request<unknown>(`/admin/ugc/articles/${enc(id)}`, { method: "PATCH", body: patch })),
	summary: async (period) =>
		UgcAnalyticsSchema.parse(
			await request<unknown>("/admin/ugc/summary", {
				query: { from: period.from || undefined, to: period.to || undefined },
			}),
		),
	markPaid: async (commissionId) =>
		UgcCommissionSchema.parse(await request<unknown>(`/admin/ugc/commissions/${enc(commissionId)}/paid`, { method: "POST" })),
});
